<template>
	<div class="exchange-school-site-index">
		<div
			class="exchange-school-site-index__header bg-black"
		>
			<div
				class="exchange-school-site-index__header-image"
			>
				<VImg
					:alt="`${schoolSite?.exchangeTitle} ${t('headerImage')}`"
					cover
					height="100%"
					width="100%"
					position="top center"
					:src="schoolSite?.headerBackground"
				/>
			</div>
			<VContainer class="exchange-school-site-index__header-content">
				<VRow
					justify="center"
				>
					<VCol cols="8">
						<div class="text-center">
							<h1 class="pb-5">
								{{ t('exchangeSchoolSite.title', { exchangeTitle: schoolSite?.exchangeTitle }) }}
							</h1>
							<p class="pb-5">
								{{ t('exchangeSchoolSite.description', { exchangeSchool: schoolSite?.schoolName }) }}
							</p>
						</div>
					</VCol>
				</VRow>
				<VRow
					justify="center"
					class="pt-6"
				>
					<VCol
						cols="12"
						md="4"
						xl="3"
					>
						<BaseField
							v-slot="{id}"
							class="px-md-1"
							:label="t('pageExchangeAthletesBrowse.searchAthletes')"
							hide-label
						>
							<VTextField
								:id="id"
								ref="fieldUsersSearch"
								v-model="usersSearch"
								bg-color="white"
								:placeholder="t('pageExchangeAthletesBrowse.searchAthletes')"
								hide-details
								data-testid="inputUsersSearch"
								autocomplete="off"
								clearable
								prepend-inner-icon="search"
							/>
						</BaseField>
					</VCol>
					<VCol
						cols="12"
						md="4"
						xl="3"
					>
						<BaseField
							v-slot="{id}"
							class=" px-md-1"
							:label="t('fields.sportLabel')"
							hide-label
						>
							<VAutocomplete
								:id="id"
								v-model="localFilters.sports"
								:items="sportOptionsFiltered"
								:placeholder="t('fields.sportPlaceholder', 0)"
								clearable
								multiple
								bg-color="white"
								item-title="name"
								item-value="id"
								class="exchange-school-site-index__sport-filter"
								data-testid="selectFilterSport"
								@update:menu="applyFilter"
								@click:clear="resetTeamFilter"
							>
								<template #selection="{ item, index }">
									<span v-if="index === 0">{{ item.title }}</span>
									<span
										v-if="index === 1"
										class="text-grey text-caption align-self-center mx-2"
									>
										( {{ t('andOthers', localFilters.sports.length - 1) }} )
									</span>
								</template>
							</VAutocomplete>
						</BaseField>
					</VCol>
				</VRow>
			</VContainer>
		</div>
		<div class="bg-white">
			<VContainer class="py-8">
				<VRow>
					<VCol class="d-flex flex-wrap ga-2 align-center pb-7">
						<div
							class="text-h4"
							data-testid="elementAthleteCount"
						>
							{{ t('athletesAvailable', totalAthletes) }}
							<span
								v-if="filteredSports.length"
								class="font-weight-regular"
							>
								{{ t('filteredBy') }}
							</span>
						</div>
						<VChip
							v-for="sport in filteredSports"
							:key="sport.id"
							closable
							variant="flat"
							label
							color="neutral-100"
							class="font-weight-semibold"
							:data-testid="`buttonRemoveFilter${sport.id}`"
							@click:close="removeFilteredSport(sport.id)"
						>
							{{ sport.name }}
							<template #close>
								<VIcon
									class="ml-n1"
									size="16"
								>
									close
								</VIcon>
							</template>
						</VChip>
						<VBtn
							v-if="filteredSports.length"
							variant="flat"
							density="comfortable"
							height="32"
							color="neutral-100"
							data-testid="buttonClearFilters"
							@click="clearTeamFilter"
						>
							{{ t('clearFilters') }}
						</VBtn>
					</VCol>
				</VRow>
				<AthletesBrowse
					v-model:length="totalAthletes"
					@work-with-me="openRegisterNowDialog"
				/>
			</VContainer>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { isEqual, cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { sports } from '@/utils/sports';
import { useExchangeSchoolSiteRegisterNowDialog } from '@/composables/useExchangeSchoolSiteRegisterNowDialog';
import { useExchangeSchoolSiteStore } from '@/stores/exchangeSchoolSiteStore';
import { useExchangeAthletesStore } from '@/stores/exchangeAthletesStore';
import { useExchangeAthletesQueryFilters } from '@/composables/useExchangeAthletesQueryFilters';
import { onBeforeRouteUpdate, useRoute } from 'vue-router';
import { BaseField } from '@teamworksdev/vuetify';
import AthletesBrowse from '@/components/exchange-school-site/AthletesBrowse.vue';

const { t } = useI18n();

const { schoolSite } = storeToRefs(useExchangeSchoolSiteStore());
const route = useRoute();
const exchangeAthletesStore = useExchangeAthletesStore();

const {
	localFilters,
	pushFilterHistory,
	replaceFilterHistory,
	updateFiltersFromQuery,
} = useExchangeAthletesQueryFilters();

function applyFilter(menuOpen: boolean) {
	if (!menuOpen) {
		const deep = !isEqual(localFilters, exchangeAthletesStore.DEFAULT_ATHLETES_REQUEST_FILTERS);
		pushFilterHistory(deep);
	}
}

function resetTeamFilter() {
	applyFilter(false);
}
// SEARCH
const usersSearch = computed<string | null>({
	get: () => exchangeAthletesStore.athletesSearch,
	set: useDebounceFn((value) => {
		exchangeAthletesStore.setAthletesSearch(value ? value : null);
	}, 500),
});

const sportOptionsFiltered = computed(() => {
	return sports.filter((sport) => schoolSite.value?.sports.includes(sport.id));
});


// FILTER CHIPS
const filteredSports = computed(() => {
	return sports.filter((sport) => exchangeAthletesStore.athletesFilters.sports.includes(sport.id));
});

const removeFilteredSport = (id: number) => {
	localFilters.sports = localFilters.sports.filter((sportId) => sportId !== id);
	applyFilter(false);
};

const clearTeamFilter = () => {
	localFilters.sports = [];
	applyFilter(false);
};

// Total Athletes
const totalAthletes = ref(0);


// REGISTER NOW DIALOG
const { open: openRegisterNowDialog } = useExchangeSchoolSiteRegisterNowDialog();

onBeforeRouteUpdate((to) => {
	updateFiltersFromQuery(to.query);
});

onBeforeMount(() => {
	if (!exchangeAthletesStore.hasActiveFilters && Object.keys(route.query).length != 0) {
		// Shared direct link - no active filters but query params exist
		updateFiltersFromQuery(route.query);
		Object.assign(localFilters, cloneDeep(exchangeAthletesStore.athletesFilters));
	} else if (exchangeAthletesStore.hasActiveFilters && Object.keys(route.query).length === 0) {
		// Returning to Browse from another page - no query params but active filters exist
		replaceFilterHistory();
	}
});
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/settings';
.exchange-school-site-index {
	--header-padding: 80px;
	margin-top:calc(var(--v-layout-top) * -1);

	&__header{
		position: relative;
		overflow: hidden;
	}
	&__header-image{
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		&::after{
			position: absolute;
			top: 0;
			left:0;
			width: 100%;
			height: 100%;
			content:'';
			background:rgba(var(--v-theme-school-header-background), 0.5);

		}
	}

	&__header-content{
		position: relative;
		padding-top:calc(var(--v-layout-top) + var(--header-padding));
		padding-bottom: calc(var(--header-padding) * 2);
	}

	&__sport-filter{
		:deep(.v-field__input){
			flex-wrap: nowrap;
		}
		:deep(.v-autocomplete__selection){
			display: inline-block;
			min-width: 0px;
			white-space: nowrap;
		}
		:deep(.v-autocomplete__selection:first-child){
			overflow: hidden;
			text-overflow: ellipsis;
			flex: 0 3 auto;
		}
	}

	@media screen and (max-width: map.get(settings.$grid-breakpoints, 'md')) {
		--header-padding: 40px;
	}
}
</style>
